import styles from './Duties.module.css';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Typography from '@mui/material/Typography';

import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOffIcon from '@mui/icons-material/TimerOff';

import React from "react";

import { DateTime } from 'luxon';
import { useInterval } from '../../hooks/useInterval.jsx';
import { CLOCK_IN_EARLY_MS, isPastHardStop, isShiftActive, pickActiveShift } from '../../utils/clockWindow.js';
import { LocalRelativeTime } from '../LocalDateTime/LocalDateTime.jsx';
import { Shift } from '../Shift/Shift.jsx';

const DutyHeader = ({ assigned }) => {
    return (
        <div className={ styles.header }>
            <span>
                <Icon component={ TimerIcon } sx={ { mr: 1 } } />
                <span>Assigned Duties: { assigned ?? 0 }</span>
            </span>
            <span>{ DateTime.now().toFormat('HH:mm:ss') }</span>
        </div>
    );
};

const getShiftStatus = (shift, patrollerId) => {
    const now = DateTime.now().toLocal();
    const nowMillis = now.toMillis();
    const today = now.toISODate();
    const shiftDate = DateTime.fromMillis(shift.start).toLocal().toISODate();

    const timelog = shift.timelogs?.find(t => t.patroller === patrollerId);

    const status = {
        // Time conditions - updated for same-day clock-in
        beforeShiftDay: today < shiftDate,
        shiftDay: today === shiftDate,
        afterShiftDay: today > shiftDate,
        duringShift: nowMillis >= shift.start && nowMillis < shift.end,
        afterShift: nowMillis >= shift.end,

        // Clock conditions
        hasStarted: !!timelog?.started,
        hasStopped: !!timelog?.stopped,

        // Derived statuses - updated logic
        get notStarted() { return this.beforeShiftDay && !this.hasStarted; },
        get readyToClockIn() { return this.shiftDay && !this.hasStarted; }, // Can clock in anytime on shift day
        get lateIn() {
            // Only late if shift has actually started and patroller hasn't clocked in
            return this.duringShift && !this.hasStarted;
        },
        // Open timelog past shift.end + 3h: the server closes it at the scheduled end, so it is closed here too.
        pastHardStop: isPastHardStop(shift, nowMillis),
        get clockedIn() { return this.hasStarted && !this.hasStopped && !this.afterShift; },
        get lateOut() { return this.hasStarted && !this.hasStopped && this.afterShift && !this.pastHardStop; },
        get clockedOut() { return this.hasStarted && (this.hasStopped || this.pastHardStop); },
        get missedShift() { return this.afterShiftDay && !this.hasStarted; },
        get isActive() { return this.clockedIn || this.lateOut; },
        get canClockIn() {
            // Can clock in anytime on shift day or after if not started
            return (this.shiftDay || this.afterShiftDay) && !this.hasStarted;
        },
        get canClockOut() { return this.hasStarted && !this.hasStopped && !this.pastHardStop; },

        // Status priority (for UI display)
        get primaryStatus() {
            if (this.lateOut) return 'late_out';
            if (this.clockedIn) return 'clocked_in';
            if (this.lateIn) return 'late_in';
            if (this.readyToClockIn) return 'ready';
            if (this.clockedOut) return 'completed';
            if (this.missedShift) return 'missed';
            if (this.notStarted) return 'upcoming';
            return 'unknown';
        }
    };

    return status;
};

// MARK: Status
const Status = ({ patroller, active, nextDuty, onOffDuty }) => {
    const now = DateTime.now().toLocal();
    const today = now.toISODate();

    if (active) {
        const status = getShiftStatus(active, patroller.id);

        // Check if both clocks are missed and no later shifts today
        const checkMissedBothClocks = () => {
            if (!status.afterShift) return false;

            const timelog = active.timelogs?.find(t => t.patroller === patroller.id);
            const bothClocksMissed = !timelog?.started && !timelog?.stopped;

            if (!bothClocksMissed) return false;

            // Check if patroller has shifts AFTER this missed shift on the same day
            const laterShiftsToday = patroller.shifts?.filter(s =>
                s.id !== active.id &&
                DateTime.fromMillis(s.start).toISODate() === today &&
                s.start > active.start // Only shifts that start after the current missed shift
            ) || [];

            // Only show alert if there are NO later shifts today
            return laterShiftsToday.length === 0;
        };

        const missedBothClocks = checkMissedBothClocks();

        // Show alert instead of button if both clocks missed and no later shifts
        if (missedBothClocks) {
            return (
                <Alert variant="filled" severity="error" sx={ { marginTop: 2 } }>
                    Missed shift. You didn&apos;t clock in for this shift.
                </Alert>
            );
        }

        // Before the clock-in window opens (shift.start - 1h) there is no Clock In button, only a note.
        if (!status.hasStarted && now.toMillis() < active.start - CLOCK_IN_EARLY_MS && status.readyToClockIn) {
            return (
                <Typography variant="body2" color="text.secondary" sx={ { textAlign: 'center' } }>
                    Clock-in opens at { DateTime.fromMillis(active.start - CLOCK_IN_EARLY_MS).toFormat('h:mm a') }. To ski before then, clock in as unscheduled for the current shift.
                </Typography>
            );
        }

        // Determine button appearance based on status
        const getButtonConfig = () => {
            if (status.lateOut) {
                return {
                    color: 'error',
                    label: 'Clock Out (Late)',
                    icon: <NotificationsIcon />,
                    action: () => onOffDuty(active)
                };
            }

            if (status.clockedIn) {
                return {
                    color: 'warning',
                    label: 'Clock Out',
                    icon: <TimerOffIcon />,
                    action: () => onOffDuty(active)
                };
            }

            if (status.lateIn) {
                return {
                    color: 'error',
                    label: 'Clock In (Late)',
                    icon: <NotificationsIcon />,
                    action: () => onOffDuty(active)
                };
            }

            if (status.readyToClockIn) {
                return {
                    color: 'success',
                    label: 'Clock In',
                    icon: <TimerIcon />,
                    action: () => onOffDuty(active)
                };
            }

            if (status.clockedOut) {
                return {
                    color: 'primary',
                    label: 'Shift Complete',
                    icon: <TimerOffIcon />,
                    disabled: true
                };
            }

            // If cannot clock in yet (before shift day), return null to hide button
            if (status.notStarted) {
                return null;
            }

            return null;
        };

        const buttonConfig = getButtonConfig();

        // Only render button if buttonConfig exists
        if (buttonConfig) {
            return (
                <Button
                    variant="contained"
                    size="large"
                    color={ buttonConfig.color }
                    sx={ {
                        width: { xs: '100%', sm: 'fit-content' },
                        height: { xs: '200%', sm: 'fit-content' },
                        fontSize: { xs: '1.2rem', sm: '1rem' },
                        padding: { xs: '1.2rem', sm: '0.75rem 1.5rem' },
                        alignSelf: 'center'
                    } }
                    onClick={ buttonConfig.action }
                    startIcon={ buttonConfig.icon }
                    disabled={ buttonConfig.disabled }
                    disableElevation
                >
                    { buttonConfig.label }
                </Button>
            );
        }

        // Show upcoming shift info when no button is displayed
        if (status.notStarted) {
            return (
                <Alert variant="filled" severity="info">
                    Next shift starts: { DateTime.fromMillis(active.start).toFormat('MMM dd') } at { DateTime.fromMillis(active.start).toFormat('HH:mm') }
                </Alert>
            );
        }
    }

    // Handle nextDuty display (existing logic)
    if (nextDuty && DateTime.fromMillis(nextDuty.start).toISODate() === today) {
        const dutyStartLocal = DateTime.fromMillis(nextDuty.start, { zone: 'local' });
        const timeToNext = dutyStartLocal.diff(now).toObject().milliseconds;

        return <Alert variant="filled" severity="success">Shift starts in <LocalRelativeTime time={ timeToNext } /></Alert>;
    }

    if (nextDuty && DateTime.fromMillis(nextDuty.start).toISODate() > today) {
        return (
            <Alert variant="filled" severity="info">
                Next shift: { DateTime.fromMillis(nextDuty.start).toFormat('MMM dd') } at { DateTime.fromMillis(nextDuty.start).toFormat('HH:mm') }
            </Alert>
        );
    }

    return <Alert variant="filled" severity="info">No assigned shifts</Alert>;
};

// MARK: Duties
export const Duties = ({ patroller, onOffDuty, onViewShift, onAssignDuty, onActiveShiftChange }) => {
    const [ now, setNow ] = React.useState(DateTime.now().toLocal().toMillis());
    const [ active, setActive ] = React.useState(null);

    const filtered = React.useMemo(() => {
        return patroller.shifts ? [ ...patroller.shifts ].sort((a, b) => a.start - b.start) : undefined;
    }, [ patroller ]);

    const nextDuty = React.useMemo(() => {
        return filtered?.find(f => {
            // If the patroller has an open timelog that is not past its hard stop, this is the current duty
            if (isShiftActive(f, patroller.id, now)) return true;

            // Otherwise, if the shift hasn't ended and the patroller has not started it yet
            const hasNotStarted = !f.timelogs?.some(t => t.patroller === patroller.id && t.started);

            return now < f.end && hasNotStarted;
        });
    }, [ filtered, now, patroller.id ]);

    useInterval(() => {
        setNow(DateTime.now().toLocal().toMillis());

        // Active shift = the newest shift with an open timelog that is within its hard stop; else nextDuty.
        setActive(pickActiveShift(patroller.shifts, patroller.id, DateTime.now().toMillis()) ?? nextDuty ?? null);
    }, 500);

    // Notify parent component when active shift changes
    React.useEffect(() => {
        if (onActiveShiftChange) {
            onActiveShiftChange(active);
        }
    }, [ active, onActiveShiftChange ]);

    return (
        <div className={ styles.dutyContainer }>
            <DutyHeader assigned={ filtered?.length } />

            <div className={ styles.duties }>
                <Status
                    onOffDuty={ onOffDuty }
                    nextDuty={ nextDuty }
                    active={ active }
                    patroller={ patroller }
                />
                
                <Divider variant="middle" sx={ { my: 2 } } flexItem />

                <Accordion defaultExpanded={ true } sx={ { width: '100%' } }>
                    <AccordionSummary expandIcon={ <ExpandMore /> }>
                        <strong>Upcoming Shifts</strong>

                        <Typography sx={ { marginLeft: 'auto', display: 'flex', alignItems: 'center' } } variant="body2" color="text.secondary">
                            { filtered?.filter(d => {
                                const status = getShiftStatus(d, patroller.id);

                                return !status.clockedOut && !status.missedShift;
                            }).length ?? 0 } upcoming shifts
                        </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ bgcolor: 'action.hover' }}>
                        { filtered?.filter(d => {
                            const status = getShiftStatus(d, patroller.id);

                            return !status.clockedOut && !status.missedShift;
                        }).length === 0 && (
                                <Alert severity="info">No upcoming shifts</Alert>
                            ) }
                        { filtered?.filter(d => {
                            const status = getShiftStatus(d, patroller.id);

                            return !status.clockedOut && !status.missedShift;
                        }).map(d => {
                            // Calculate shift status for each shift from Duties context
                            const shiftStatus = getShiftStatus(d, patroller.id);

                            return (
                                <Shift
                                    key={ d.id }
                                    shift={ d }
                                    patroller={ patroller }
                                    assigned={ d }
                                    nextDuty={ nextDuty }
                                    onViewShift={ onViewShift }
                                    onAssignDuty={ onAssignDuty }
                                    shiftStatus={ shiftStatus } // Pass status as indicator it's from Duties
                                />
                            );
                        }) }
                    </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded={ false } sx={ { mb: 1, width: '100%' } }>
                    <AccordionSummary expandIcon={ <ExpandMore /> }>
                        <strong>Completed Shifts</strong>

                        <Typography sx={ { marginLeft: 'auto', display: 'flex', alignItems: 'center' } } variant="body2" color="text.secondary">
                            { filtered?.filter(d => {
                                const status = getShiftStatus(d, patroller.id);

                                return status.clockedOut || status.missedShift;
                            }).length ?? 0 } completed shifts
                        </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={ { bgcolor: 'action.hover' } }>
                        { filtered?.filter(d => {
                            const status = getShiftStatus(d, patroller.id);

                            return status.clockedOut || status.missedShift;
                        }).length === 0 && (
                                <Alert severity="info">No completed shifts</Alert>
                            ) }

                        { filtered?.filter(d => {
                            const status = getShiftStatus(d, patroller.id);

                            return status.clockedOut || status.missedShift;
                        }).map(d => {
                            // Calculate shift status for each shift from Duties context
                            const shiftStatus = getShiftStatus(d, patroller.id);

                            return (
                                <Shift
                                    key={ d.id }
                                    shift={ d }
                                    patroller={ patroller }
                                    assigned={ d }
                                    nextDuty={ nextDuty }
                                    onViewShift={ onViewShift }
                                    onAssignDuty={ onAssignDuty }
                                    shiftStatus={ shiftStatus } // Pass status as indicator it's from Duties
                                />
                            );
                        }) }
                    </AccordionDetails>
                </Accordion>
            </div>
        </div>
    );
};